import React from "react";
import Link from "next/link";
import { Section } from "@/components/ui/Section";
import { Typography } from "@/components/ui/Typography";
import { Button } from "@/components/ui/Button";
import { baseUrl } from "@/lib/data";

export interface PricingSectionProps extends React.HTMLAttributes<HTMLElement> {
  title?: string;
  subtitle?: string;
  professionalPriceLabel?: string;
  professionalCtaLabel?: string;
  professionalCtaHref?: string;
  enterpriseTitle?: string;
  enterpriseDescriptionPrimary?: string;
  enterpriseDescriptionSecondary?: string;
  enterpriseCtaLabel?: string;
  enterpriseCtaHref?: string;
}

const PricingSection: React.FC<PricingSectionProps> = ({
  title = "Pocket-friendly Pricing Plans",
  subtitle = "Simple pricing. Start your free trial and then pay as you use!",
  professionalPriceLabel = "₹ 900",
  professionalCtaLabel = "Try Free For 14 Days",
  professionalCtaHref = "https://console.pickcel.com/auth/register",
  enterpriseTitle = "Enterprise",
  enterpriseDescriptionPrimary = "Want more capabilities and you have more than 250 displays?",
  enterpriseDescriptionSecondary = "Talk to our sales team.",
  enterpriseCtaLabel = "Talk To Us",
  enterpriseCtaHref = `/company/contact`,
  className,
  ...props
}) => {
  return (
    <Section variant="default" className={className} {...props}>
      <div className="flex flex-col lg:flex-row justify-between items-center mb-16">
        <div className="text-center lg:text-left">
          <Typography variant="h2" as="h2" className="mb-4">
            {title}
          </Typography>
          <Typography variant="large" as="p">
            {subtitle}
          </Typography>
        </div>
        <Link href="/pricing" className="border border-gray-600 rounded-lg px-4 py-2 mt-5 lg:mt-0">
          View All →
        </Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 w-full">
        {/* Professional Plan */}
        <div className="bg-gray-900 text-white rounded-2xl p-8 relative">
          <Typography variant="h3" as="h3" className="mb-4 text-white">
            Professional
          </Typography>
          <Typography variant="body" as="p" className="mb-6 text-gray-300">
            All features you require to run a professional digital signage network
          </Typography>
          <div className="flex flex-col items-start gap-4 md:flex-row md:justify-between md:items-center">
            <div className="text-sm">
              <span className="font-bold">{professionalPriceLabel}</span>
              <span className="text-gray-300">/device/month</span>
            </div>
            <Link href={professionalCtaHref} target="_blank" rel="noopener noreferrer">
              <Button
                variant="accent"
                size="md"
              >
                {professionalCtaLabel}
              </Button>
            </Link>
          </div>
        </div>

        {/* Enterprise Plan */}
        <div className="bg-white border-2 border-gray-200 rounded-2xl p-8">
          <Typography variant="h3" as="h3" className="mb-4">
            {enterpriseTitle}
          </Typography>
          <Typography variant="body" as="p" className="mb-1 text-gray-600">
            {enterpriseDescriptionPrimary}
          </Typography>
          <Typography variant="body" as="p" className="mb-2 text-gray-600">
            {enterpriseDescriptionSecondary}
          </Typography>
          <div className="flex flex-row justify-start md:justify-end w-full">
            <Link href={enterpriseCtaHref} >
              <Button variant="outlineAccent" size="md" className="w-[180px]">
                {enterpriseCtaLabel}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Section>
  );
};

PricingSection.displayName = "PricingSection";

export { PricingSection };
